import Day from '@awesomecomponents/day-js';
import * as Yup from 'yup';
import { DATE_FORMAT } from '../../utils/AppConstants';

/**
 * Yup validationSchema for AddFamilyModal.
 * @param {object} validationMessages
 * @param {array} dependents
 * @param {number} studentMaxAge
 */
export const getAddFamilyValidationSchema = (validationMessages, dependents, studentMaxAge) => {
    return {
        firstName: Yup.string()
            .matches(/^[a-zA-ZÀ-ÿ-. ]*$/, validationMessages.firstNameBlank)
            .required(validationMessages.firstNameBlank),
        lastName: Yup.string()
            .matches(/^[a-zA-ZÀ-ÿ-. ]*$/, validationMessages.lastNameblank)
            .required(validationMessages.lastNameblank),
        dateOfBirth: Yup.object({
            day: Yup.number().required('Please enter day'),
            month: Yup.string().required('Please enter month'),
            year: Yup.number().required('Please enter year'),
            date: Yup.date().required(validationMessages.dateOfBirthBlank),
        }),
        gender: Yup.string().required(validationMessages.sexBlank),
        familyMemberType: Yup.string().oneOf(['CHILD', 'SPOUSE']).required(validationMessages.relationshipBlank),
        additionReason: Yup.string().required(validationMessages.reasonForAdditionBlank),
        effectiveDate: Yup.date().when(['additionReason'], {
            is: (additionReason) => additionReason !== 'JoiningMyNewPlan' && additionReason !== 'Birth',
            then: Yup.date()
                .transform((value, originalValue) => Day.parse(originalValue, DATE_FORMAT.MUX_LOCALIZED).toDate())
                .required(validationMessages.missingCoverageStartDate),
        }),
        hasOtherCoverage: Yup.string().oneOf(['true', 'false']).required(validationMessages.cobBenefitBlank),
        cobProvider: Yup.string().when('hasOtherCoverage', {
            is: (hasOtherCoverage) => hasOtherCoverage === 'true',
            then: Yup.string().oneOf(['MANULIFE', 'OTHER']).required(validationMessages.cobProviderBlank),
        }),
        cobContractNumber: Yup.number().when(['hasOtherCoverage', 'cobProvider'], {
            is: (hasOtherCoverage, cobProvider) => cobProvider === 'MANULIFE' && hasOtherCoverage === 'true',
            then: Yup.number().typeError(validationMessages.cobPolicyBlank).required(validationMessages.cobPolicyBlank),
        }),
        cobCertificateNumber: Yup.string().when(['hasOtherCoverage', 'cobProvider'], {
            is: (hasOtherCoverage, cobProvider) => cobProvider === 'MANULIFE' && hasOtherCoverage === 'true',
            then: Yup.string()
                .matches(/^[aA-zZ0-9]{0,9}$/, validationMessages.cobCertBlank)
                .required(validationMessages.cobCertBlank),
        }),
        kindOfCoverageSelected: Yup.object().when('hasOtherCoverage', {
            is: (hasOtherCoverage) => hasOtherCoverage === 'true',
            then: Yup.object().test('is-cobCoverageSelectBlank', validationMessages.cobCoverageSelectBlank, (value) =>
                Object.values(value).includes(true)
            ),
        }),
        familyMemberAccuracyAgreement: Yup.string().oneOf(['true'], validationMessages.certifyCheckBox),
        duplicateChild: Yup.mixed().when(['firstName', 'lastName', 'dateOfBirth', 'familyMemberType'], {
            is: (firstName, lastName, dateOfBirth, familyMemberType) =>
                familyMemberType === 'CHILD' &&
                dependents.some((familyMember) => {
                    return (
                        familyMember.status !== 'TERMINATED' &&
                        familyMember.firstName?.toUpperCase() === firstName?.toUpperCase() &&
                        familyMember.lastName?.toUpperCase() === lastName?.toUpperCase() &&
                        familyMember.birthDate === new Day(dateOfBirth?.date).format('en', DATE_FORMAT.ISO_STANDARD)
                    );
                }),
            then: Yup.string().required(validationMessages.duplicateActiveDependent),
        }),
        terminatedDependent: Yup.mixed().when(['firstName', 'lastName', 'dateOfBirth'], {
            is: (firstName, lastName, dateOfBirth) =>
                dependents.some((familyMember) => {
                    return (
                        familyMember.status === 'TERMINATED' &&
                        familyMember.firstName?.toUpperCase() === firstName?.toUpperCase() &&
                        familyMember.lastName?.toUpperCase() === lastName?.toUpperCase() &&
                        familyMember.birthDate === new Day(dateOfBirth?.date).format('en', DATE_FORMAT.ISO_STANDARD)
                    );
                }),
            then: Yup.string().required(validationMessages.duplicateTerminatedDependent),
        }),
        overageDependent: Yup.mixed().when(['dateOfBirth', 'familyMemberType'], {
            is: (dateOfBirth, familyMemberType) =>
                new Day(dateOfBirth?.date).age() >= studentMaxAge && familyMemberType === 'CHILD',
            then: Yup.string().required(validationMessages.studentMaxAge),
        }),
    };
};

/**
 * Yup validationSchema for EditFamilyModal.
 * @param {object} validationMessages
 */
export const getEditFamilyValidationSchema = (validationMessages) => {
    return {
        firstName: Yup.string()
            .matches(/^[a-zA-ZÀ-ÿ-. ]*$/, validationMessages.firstNameBlank)
            .required(validationMessages.firstNameBlank),
        lastName: Yup.string()
            .matches(/^[a-zA-ZÀ-ÿ-. ]*$/, validationMessages.lastNameblank)
            .required(validationMessages.lastNameblank),
        dateOfBirth: Yup.object({
            day: Yup.number().required('Please enter day'),
            month: Yup.string().required('Please enter month'),
            year: Yup.number().required('Please enter year'),
            date: Yup.date().required(validationMessages.dateOfBirthBlank),
        }),
        gender: Yup.string().required(validationMessages.sexBlank),
        familyMemberType: Yup.string().oneOf(['CHILD', 'SPOUSE']).required(validationMessages.relationshipBlank),
        hasOtherCoverage: Yup.string().oneOf(['true', 'false']).required(validationMessages.cobBenefitBlank),
        cobProvider: Yup.string().when('hasOtherCoverage', {
            is: (hasOtherCoverage) => hasOtherCoverage === 'true',
            then: Yup.string().oneOf(['MANULIFE', 'OTHER']).required(validationMessages.cobProviderBlank),
        }),
        cobContractNumber: Yup.number().when(['hasOtherCoverage', 'cobProvider'], {
            is: (hasOtherCoverage, cobProvider) => cobProvider === 'MANULIFE' && hasOtherCoverage === 'true',
            then: Yup.number().typeError(validationMessages.cobPolicyBlank).required(validationMessages.cobPolicyBlank),
        }),
        cobCertificateNumber: Yup.string().when(['hasOtherCoverage', 'cobProvider'], {
            is: (hasOtherCoverage, cobProvider) => cobProvider === 'MANULIFE' && hasOtherCoverage === 'true',
            then: Yup.string()
                .matches(/^[aA-zZ0-9]{0,9}$/, validationMessages.cobCertBlank)
                .required(validationMessages.cobCertBlank),
        }),
        kindOfCoverageSelected: Yup.object().when('hasOtherCoverage', {
            is: (hasOtherCoverage) => hasOtherCoverage === 'true',
            then: Yup.object().test('is-cobCoverageSelectBlank', validationMessages.cobCoverageSelectBlank, (value) =>
                Object.values(value).includes(true)
            ),
        }),
        familyMemberAccuracyAgreement: Yup.string().oneOf(['true'], validationMessages.certifyCheckBox),
    };
};

/**
 * Yup validationSchema for ExtendCoverageModal.
 * @param {object} validationMessages
 */
export const getExtendCoverageValidationSchema = (validationMessages) => {
    return {
        extensionReason: Yup.string()
            .oneOf(['fullTimeStudent', 'disabled'])
            .required(validationMessages.studentCoverageExtensionReasonBlank),

        familyMemberAccuracyAgreement: Yup.string().oneOf(['true'], validationMessages.certifyCheckBox),
    };
};
